import type { MentionGroup, MentionOption } from '../../ui/tiptap/extensions';
import type { ResourcePickerCategory, ResourcePickerItem } from '../types';

/** Group ids the @ menu emits, and the picker category each maps onto. */
const GROUP_ID_CATEGORY: Record<string, ResourcePickerCategory> = {
  tool: 'tool',
  workflow: 'workflow',
  'resource-agent': 'agent',
  'your-agents': 'agent',
  agents: 'agent',
  'resource-document': 'document',
  'resource-folder': 'folder',
  'resource-trigger': 'trigger',
  'resource-dataset': 'dataset',
  'coco-input': 'input',
};

/**
 * Map a group id (from `MentionOption.group`) to a `ResourcePickerCategory`.
 * Unknown ids fall back to `tool`, which is where ungrouped options belong.
 */
const groupIdToCategory = (groupId?: string): ResourcePickerCategory =>
  groupId === undefined ? 'tool' : GROUP_ID_CATEGORY[groupId] ?? 'tool';

/**
 * Build a category → icon lookup from the MentionGroup definitions, so the
 * picker's group headers reuse the icons the @ menu already defines. Groups
 * with an id the picker doesn't recognize are skipped rather than folded into
 * `tool` — otherwise an unknown group's icon would replace the wrench.
 */
export const buildCategoryIconMap = (
  groups?: MentionGroup[]
): Partial<Record<ResourcePickerCategory, React.ReactNode>> => {
  const icons: Partial<Record<ResourcePickerCategory, React.ReactNode>> = {};
  if (groups === undefined) {
    return icons;
  }
  for (const g of groups) {
    const category = GROUP_ID_CATEGORY[g.id];
    if (g.icon === undefined || category === undefined) {
      continue;
    }
    icons[category] = g.icon;
  }
  return icons;
};

/**
 * Build a subgroup-id → icon lookup from the MentionGroup definitions.
 * Used to inject provider logos onto tool picker items.
 */
export const buildSubgroupIconMap = (
  groups?: MentionGroup[]
): Map<string, React.ReactNode> => {
  const map = new Map<string, React.ReactNode>();
  if (groups === undefined) {
    return map;
  }
  for (const g of groups) {
    if (g.subgroups === undefined) {
      continue;
    }
    for (const sg of g.subgroups) {
      if (sg.icon !== undefined) {
        map.set(sg.id, sg.icon);
      }
    }
  }
  return map;
};

/**
 * Convert a `MentionOption` (from `useCocoMentions`) to a `ResourcePickerItem`.
 *
 * @param subgroupIcons — optional map from subgroup id → icon (from `buildSubgroupIconMap`)
 */
export const mentionOptionToPickerItem = <T>(
  option: MentionOption<T> & {
    providerLabel?: string;
  },
  _subgroupIcons?: Map<string, React.ReactNode>
): ResourcePickerItem<T> => ({
  id: option.referenceId ?? option.label,
  label: option.label,
  category: groupIdToCategory(option.group),
  provider: option.subgroup,
  providerLabel: (option as { providerLabel?: string }).providerLabel,
  data: option.data,
  color: option.color,
  available: option.available,
  detail: option.detail,
  typeLabel: option.typeLabel,
  badge: option.badge,
  searchKeywords: [
    option.typeLabel,
    (option as { providerLabel?: string }).providerLabel,
    option.subgroup,
  ].filter((k): k is string => k !== undefined),
});
